import { useState, useMemo, useCallback, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSSE } from '../../hooks/useSSE'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCorners,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import { useProject } from './ProjectLayout'
import { StoryDrawer } from './StoryDrawer'
import {
  Avatar,
  Chip,
  cn,
} from '../../design'
import {
  listStories,
  listWorkflowStates,
  updateStory,
  createStory,
  type Story,
  type WorkflowState,
} from '../../api/stories'
import { lookupEpics, type EpicLookup } from '../../api/users'

/**
 * Phase 6 — Kanban V2 (swimlanes by epic).
 * rows = epics, cols = workflow_states (project's configured statuses).
 * Drag card between columns → optimistic status_id update; rollback on server error.
 */
export default function Board() {
  const project = useProject()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeStoryId, setActiveStoryId] = useState<number | null>(null)

  const storyKey = params.get('story')

  const wfQ = useQuery({
    queryKey: ['workflow-states', project.id],
    queryFn: () => listWorkflowStates(project.id),
    staleTime: 60_000,
  })
  const epicsQ = useQuery({
    queryKey: ['lookups', 'epics', project.id],
    queryFn: () => lookupEpics(project.id),
    staleTime: 60_000,
  })
  const storiesQ = useQuery({
    queryKey: ['stories', 'board', project.id],
    queryFn: () => listStories({ project_id: project.id, per_page: 200, sort: 'sort_order' }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  // Live updates: SSE invalidates the board on any story event in this project.
  useSSE({
    projectId: project.id,
    invalidate: {
      'story.created':         [['stories'], ['dashboard', project.id]],
      'story.updated':         [['stories'], ['dashboard', project.id]],
      'story.status_changed':  [['stories'], ['dashboard', project.id]],
      'story.deleted':         [['stories'], ['dashboard', project.id]],
    },
  })

  // Group stories by epic_id (or 'none') and status_id
  const grouped = useMemo(() => {
    const map: Record<string, Record<number, Story[]>> = {}
    const stories = storiesQ.data?.data ?? []
    for (const s of stories) {
      const ek = s.epic_id !== null ? String(s.epic_id) : 'none'
      if (!map[ek]) map[ek] = {}
      if (!map[ek][s.status_id]) map[ek][s.status_id] = []
      map[ek][s.status_id]!.push(s)
    }
    return map
  }, [storiesQ.data])

  // Optimistic update for status_id change on drag
  const updateMut = useMutation({
    mutationFn: ({ id, status_id }: { id: number; status_id: number }) =>
      updateStory(id, { status_id }),
    onMutate: async ({ id, status_id }) => {
      await qc.cancelQueries({ queryKey: ['stories', 'board', project.id] })
      const prev = qc.getQueryData<{ data: Story[] }>(['stories', 'board', project.id])
      if (prev) {
        qc.setQueryData(['stories', 'board', project.id], {
          ...prev,
          data: prev.data.map(s => s.id === id ? { ...s, status_id } : s),
        })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['stories', 'board', project.id], ctx.prev)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['stories'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const onDragStart = useCallback((e: DragStartEvent) => {
    setActiveStoryId(Number(e.active.id))
  }, [])

  const onDragEnd = useCallback((e: DragEndEvent) => {
    setActiveStoryId(null)
    const { active, over } = e
    if (!over) return
    const storyId = Number(active.id)
    // over.id format: "drop:<epicKey>:<statusId>"
    const overId = String(over.id)
    if (!overId.startsWith('drop:')) return
    const [, , statusIdStr] = overId.split(':')
    const newStatusId = Number(statusIdStr)
    const stories = storiesQ.data?.data ?? []
    const story = stories.find(s => s.id === storyId)
    if (!story || story.status_id === newStatusId) return
    updateMut.mutate({ id: storyId, status_id: newStatusId })
  }, [storiesQ.data, updateMut])

  function openStory(key: string) {
    const next = new URLSearchParams(params)
    next.set('story', key)
    setParams(next, { replace: true })
    setDrawerOpen(true)
  }
  function closeStory() {
    setDrawerOpen(false)
    const next = new URLSearchParams(params)
    next.delete('story')
    setParams(next, { replace: true })
  }
  // Auto-open if URL has ?story=
  useMemo(() => {
    if (storyKey && !drawerOpen) setDrawerOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyKey])

  const states = wfQ.data ?? []
  const epics = epicsQ.data ?? []
  // Add a synthetic "no epic" lane only if there are stories without an epic
  const hasUnassigned = (storiesQ.data?.data ?? []).some(s => s.epic_id === null)
  const lanes: Array<EpicLookup | { id: 'none'; name: string; color: string | null; status: string }> = [
    ...epics,
    ...(hasUnassigned ? [{ id: 'none' as const, name: 'No epic', color: '#9aa0a6', status: 'planned' }] : []),
  ]

  if (wfQ.isLoading || storiesQ.isLoading || epicsQ.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-border border-t-accent animate-spin" />
      </div>
    )
  }

  const activeStory = activeStoryId
    ? (storiesQ.data?.data ?? []).find(s => s.id === activeStoryId)
    : null

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="flex flex-col h-full">
        <div className="px-6 py-4 flex items-end justify-between border-b border-border bg-bg-elevated">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Board</h2>
            <p className="text-xs text-fg-subtle">Swimlanes by epic. Drag cards between columns to update status.</p>
          </div>
          <div className="text-xs text-fg-subtle">
            {storiesQ.data?.data.length ?? 0} stories
            {updateMut.isPending && <span className="ml-2 text-accent">syncing…</span>}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {/* Header row: empty corner + column headers */}
          <div
            className="grid gap-3 mb-3"
            style={{ gridTemplateColumns: `170px repeat(${states.length}, minmax(220px, 1fr))` }}
          >
            <div />
            {states.map(s => (
              <ColumnHeader key={s.id} state={s} count={countInColumn(grouped, s.id)} />
            ))}

            {/* Lane rows */}
            {lanes.map(lane => {
              const laneKey = String(lane.id)
              const laneStories = grouped[laneKey] ?? {}
              const laneTotal = Object.values(laneStories).reduce((acc, arr) => acc + arr.length, 0)
              const laneDone = states
                .filter(s => s.category === 'done')
                .reduce((acc, s) => acc + (laneStories[s.id]?.length ?? 0), 0)

              return (
                <SwimLane
                  key={laneKey}
                  laneId={laneKey}
                  name={lane.name}
                  color={lane.color}
                  total={laneTotal}
                  done={laneDone}
                  states={states}
                  storiesByStatus={laneStories}
                  projectId={project.id}
                  onCardClick={openStory}
                />
              )
            })}
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeStory ? <StoryCard story={activeStory} dragging /> : null}
      </DragOverlay>

      <StoryDrawer
        storyKey={storyKey}
        projectId={project.id}
        open={drawerOpen}
        onOpenChange={(o) => { if (!o) closeStory(); else setDrawerOpen(true) }}
      />
    </DndContext>
  )
}

// ─── Components ────────────────────────────────────────────────────────────

function ColumnHeader({ state, count }: { state: WorkflowState; count: number }) {
  return (
    <div className="px-3 py-2 border-b-2 border-border flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
        {state.name}
      </span>
      <span className="font-mono text-[10px] text-fg-subtle">{count}</span>
    </div>
  )
}

function SwimLane({
  laneId,
  name,
  color,
  total,
  done,
  states,
  storiesByStatus,
  projectId,
  onCardClick,
}: {
  laneId: string
  name: string
  color: string | null
  total: number
  done: number
  states: WorkflowState[]
  storiesByStatus: Record<number, Story[]>
  projectId: number
  onCardClick: (key: string) => void
}) {
  const pct = total > 0 ? (done / total) * 100 : 0
  const epicId = laneId === 'none' ? null : Number(laneId)

  return (
    <>
      {/* Lane label cell */}
      <div
        className="rounded-lg p-3 border border-border"
        style={{
          backgroundColor: color ? `${color}18` : 'var(--color-bg-muted)',
          borderColor: color ? `${color}40` : undefined,
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color ?? '#9aa0a6' }} />
          <div className="text-sm font-semibold tracking-tight truncate" title={name}>
            {name}
          </div>
        </div>
        <div className="text-[10px] font-mono text-fg-subtle">
          {total} {total === 1 ? 'story' : 'stories'}
        </div>
        <div className="h-1 mt-2 rounded-full bg-bg overflow-hidden">
          <div
            className="h-full"
            style={{ width: `${pct}%`, backgroundColor: color ?? 'var(--color-fg)' }}
          />
        </div>
      </div>

      {/* Cells */}
      {states.map(state => (
        <DropCell
          key={state.id}
          dropId={`drop:${laneId}:${state.id}`}
          stories={storiesByStatus[state.id] ?? []}
          state={state}
          epicId={epicId}
          projectId={projectId}
          onCardClick={onCardClick}
        />
      ))}
    </>
  )
}

function DropCell({
  dropId,
  stories,
  state,
  epicId,
  projectId,
  onCardClick,
}: {
  dropId: string
  stories: Story[]
  state: WorkflowState
  epicId: number | null
  projectId: number
  onCardClick: (key: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId })

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-lg p-2 min-h-[100px] flex flex-col gap-2 transition-colors',
        'border border-dashed',
        state.category === 'done' ? 'bg-success/5 border-success/30' : 'bg-bg-muted/30 border-border',
        isOver && 'bg-accent-soft border-accent ring-2 ring-accent/30',
      )}
    >
      {stories.map(s => (
        <DraggableCard key={s.id} story={s} onCardClick={onCardClick} />
      ))}
      <QuickAdd
        projectId={projectId}
        epicId={epicId}
        statusId={state.id}
      />
    </div>
  )
}

function DraggableCard({
  story,
  onCardClick,
}: {
  story: Story
  onCardClick: (key: string) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: story.id })
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Treat click without drag as open. dnd-kit invokes onClick after drag too — guard.
        if (!isDragging) onCardClick(story.key)
        e.stopPropagation()
      }}
      className={cn(
        'cursor-grab',
        isDragging && 'opacity-30',
      )}
    >
      <StoryCard story={story} />
    </div>
  )
}

function StoryCard({ story, dragging }: { story: Story; dragging?: boolean }) {
  const priorityDot: Record<string, string> = {
    high: 'bg-danger',
    med:  'bg-warning',
    low:  'bg-fg-disabled',
  }
  return (
    <div
      className={cn(
        'rounded-md border border-border bg-bg-elevated p-2.5 shadow-sm select-none',
        'hover:shadow-md transition-shadow',
        dragging && 'rotate-1 shadow-lg ring-2 ring-accent/30',
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] text-fg-subtle">{story.key}</span>
        <span className={cn('h-2 w-2 rounded-full', priorityDot[story.priority])} />
      </div>
      <div className="text-sm text-fg leading-snug line-clamp-2 mb-2">{story.title}</div>
      <div className="flex items-center gap-1.5">
        {story.assignee_name && (
          <Avatar size="xs" name={story.assignee_name} color={story.assignee_avatar_color ?? '#9aa0a6'} />
        )}
        {story.estimate_points !== null && (
          <Chip size="sm" tone="neutral" className="font-mono text-[10px]">
            {story.estimate_points}
          </Chip>
        )}
        {story.due_date && (
          <span className="text-[10px] text-fg-subtle ml-auto">
            {new Date(story.due_date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
    </div>
  )
}

function QuickAdd({
  projectId,
  epicId,
  statusId,
}: {
  projectId: number
  epicId: number | null
  statusId: number
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')

  const createMut = useMutation({
    mutationFn: (title: string) =>
      createStory({
        project_id: projectId,
        title,
        status_id: statusId,
        epic_id: epicId ?? undefined,
      } as Parameters<typeof createStory>[0]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stories'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setDraft('')
      setOpen(false)
    },
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!draft.trim()) return
    createMut.mutate(draft.trim())
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] text-fg-subtle hover:text-fg flex items-center justify-center gap-1 py-1 rounded-md hover:bg-bg-muted transition-colors"
      >
        <Plus size={10} /> Add
      </button>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-1">
      <textarea
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') { setOpen(false); setDraft('') }
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(e as unknown as FormEvent) }
        }}
        rows={2}
        placeholder="Story title…"
        className="w-full text-sm rounded-md border border-border bg-bg-elevated px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
      />
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={() => { setOpen(false); setDraft('') }}
          className="text-[11px] text-fg-subtle hover:text-fg px-2 py-0.5"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!draft.trim() || createMut.isPending}
          className="text-[11px] bg-accent text-accent-fg px-2 py-1 rounded hover:bg-accent-hover disabled:opacity-50"
        >
          {createMut.isPending ? '…' : 'Add'}
        </button>
      </div>
    </form>
  )
}

function countInColumn(grouped: Record<string, Record<number, Story[]>>, statusId: number): number {
  let total = 0
  for (const lane of Object.values(grouped)) {
    total += lane[statusId]?.length ?? 0
  }
  return total
}
